import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ComponentLogin.css';

// Mismo ancho de ventana que el cooldown por cuenta del backend (COOLDOWN_SECONDS en
// ForgotPassword.js) -- coherencia visual nada más, esto es puramente UX (persistido en
// localStorage, por navegador). La protección real contra abuso vive en el servidor (cooldown
// por cuenta en Redis + rate limit por IP en el router) -- nada impide llamar al endpoint
// directo sin pasar por este formulario, así que este timer no reemplaza esa defensa.
const COOLDOWN_MS = 10 * 60 * 1000;
const STORAGE_KEY = 'forgotPasswordCooldownUntil';

function readStoredCooldown() {
    const until = Number(localStorage.getItem(STORAGE_KEY) || 0);
    return Math.max(0, until - Date.now());
}

function formatRemaining(ms) {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

// Extraído como componente propio (no inline en Login.jsx, que ya roza el límite de 500
// líneas operativas -- ver policies/code.md) -- responsabilidad separable: pedir un
// identificador (usuario o correo) y mostrar la misma confirmación genérica sin importar si la
// cuenta existe (POST /api/forgot-password nunca distingue, ver ForgotPassword.js backend --
// distinguir sería enumeración de usuarios, mismo criterio que Login.js/2002).
export default function ForgotPasswordForm({ onBack }) {
    const { t } = useTranslation();
    const [identifier, setIdentifier] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState(readStoredCooldown);

    useEffect(() => {
        const interval = setInterval(() => setCooldownRemaining(readStoredCooldown()), 1000);
        return () => clearInterval(interval);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await fetch('/api/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ identifier }),
            });
        } catch {
            // Sin distinción de error acá tampoco -- ver nota de arriba, el mensaje siempre es
            // el mismo genérico de éxito.
        } finally {
            setIsLoading(false);
            localStorage.setItem(STORAGE_KEY, String(Date.now() + COOLDOWN_MS));
            setCooldownRemaining(COOLDOWN_MS);
        }
    };

    const onCooldown = cooldownRemaining > 0;

    return (
        <div style={{ position: 'relative', width: '100%', maxWidth: '377px' }}>
            <form onSubmit={handleSubmit} className="component-login-container" style={{ margin: 0, maxWidth: '100%' }}>
                <h2 className="login-title">{t('auth.forgotPasswordTitle')}</h2>
                <p className="login-subtitle">
                    {onCooldown ? t('auth.forgotPasswordSent') : t('auth.forgotPasswordSubtitle')}
                </p>

                {onCooldown && (
                    <p className="login-subtitle" style={{ marginTop: '-1rem' }}>
                        {t('auth.forgotPasswordCooldownTimer', { time: formatRemaining(cooldownRemaining) })}
                    </p>
                )}

                {!onCooldown && (
                    <div className="input-group">
                        <label className="input-label">{t('auth.forgotPasswordIdentifierLabel')}</label>
                        <input
                            type="text"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            className="input-field"
                            placeholder={t('auth.forgotPasswordIdentifierPlaceholder')}
                            required
                        />
                    </div>
                )}

                {!onCooldown && (
                    <button type="submit" className="certify-btn" disabled={isLoading} style={{ width: '100%', marginTop: '9px' }}>
                        {isLoading ? t('auth.forgotPasswordSending') : t('auth.forgotPasswordSubmit')}
                    </button>
                )}

                <button
                    type="button"
                    onClick={onBack}
                    style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '0.8rem', cursor: 'pointer', marginTop: '0.5rem' }}
                >
                    {t('auth.backToLogin')}
                </button>
            </form>
        </div>
    );
}
